import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Target, Globe2, Award, Sparkles } from "lucide-react";
import PageHero from "../components/PageHero";
import { ABOUT_IMAGE, BRAND } from "../lib/constants";

const VALUES = [
  { icon: Target, title: "Clarity before complexity", desc: "We simplify the problem, define success, and focus investment where it creates the strongest operational value." },
  { icon: Globe2, title: "Ready for global growth", desc: "Regions, languages, providers, and regulatory needs are considered early in the product architecture." },
  { icon: Award, title: "Experienced ownership", desc: "Specialists remain accountable for decisions, delivery quality, and a dependable handover to your team." },
  { icon: Sparkles, title: "Practical innovation", desc: "Proven foundations support the platform while new ideas are applied where they make a measurable difference." },
];

const TIMELINE = [
  { year: "01", title: "Understand the operation", desc: "We start with your business model, client journey, internal teams, and current technology." },
  { year: "02", title: "Set the right foundation", desc: "Product, infrastructure, compliance, and integration decisions become one connected blueprint." },
  { year: "03", title: "Deliver visible progress", desc: "Short build cycles give stakeholders working software to review before assumptions become expensive." },
  { year: "04", title: "Launch with confidence", desc: "Testing, migration, training, and cutover support prepare the platform for real operations." },
  { year: "05", title: "Keep improving", desc: "Ongoing support and optimization help your technology evolve alongside the business." },
];

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="About Us"
        breadcrumb={[{ label: "About" }]}
        title={<>Broker ambition meets <span className="text-gradient-cyan italic">expert delivery.</span></>}
        lede={`${BRAND.name} brings market context, product design, engineering, and integration expertise together to simplify complex broker technology decisions.`}
      />

      {/* Big quote / mission */}
      <section className="py-16 border-y border-white/10">
        <div className="max-w-5xl mx-auto px-6">
          <motion.blockquote
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium leading-snug theme-heading"
          >
            "The best broker technology does more than add features. It makes daily work clearer,
            client journeys smoother, and the next stage of growth easier to reach."
          </motion.blockquote>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#00C2FF] mt-6">
            — {BRAND.name} founding team
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-10">
            <span className="w-10 h-px bg-[#00C2FF]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#00C2FF] font-bold">
              / How we work
            </span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="p-7 rounded-2xl theme-panel border hover:border-[#00C2FF]/40 transition-colors"
                data-testid={`about-value-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#00C2FF]/10 border border-[#00C2FF]/30 flex items-center justify-center text-[#00C2FF] mb-5">
                  <v.icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-lg font-semibold mb-2">{v.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold leading-tight tracking-tighter mb-14">
            One connected <span className="text-gradient-cyan italic">delivery journey.</span>
          </h2>
          <div className="relative">
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-gradient-to-b from-[#00C2FF] via-[#00C2FF]/30 to-transparent" />
            {TIMELINE.map((t, i) => (
              <motion.div
                key={t.year}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="relative pl-10 pb-10 last:pb-0"
                data-testid={`about-timeline-${i}`}
              >
                <div className="absolute left-0 top-1.5 w-4 h-4 rounded-full bg-[#00C2FF] glow-ring" />
                <div className="font-mono text-xs text-[#00C2FF] tracking-[0.25em] uppercase">{t.year}</div>
                <h3 className="font-display text-xl font-semibold mt-2">{t.title}</h3>
                <p className="text-sm text-gray-400 mt-2 leading-relaxed">{t.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <img src={ABOUT_IMAGE} alt="" className="w-full h-64 object-cover rounded-3xl border border-white/10 mb-10" />
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            Looking for the right starting point?
          </h2>
          <p className="text-gray-400 mt-3 max-w-xl mx-auto">
            Tell us what needs to change, and we&apos;ll help turn it into a practical next step.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta-contact"
            className="btn-cyan inline-flex items-center gap-2 mt-8 px-8 py-3.5 rounded-full font-semibold"
          >
            Book a discovery call <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
